import { useCallback } from 'react';

import { useAppDispatch } from 'src/app/store/hooks';
import {
  MembershipRead,
  MembershipWrite,
  PatchedMembershipWrite,
  useLazyMembersListQuery,
  useMembersCreateMutation,
  useMembersDestroyMutation,
  useMembersPartialUpdateMutation,
  useMembersUndeleteCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { setSelectedMember, setShowMemberForm } from '../member.slice';
import { generateMemberAddSuccessToast } from './generateMemberAddSuccessToast';
import { generateMemberDeleteSuccessToast } from './generateMemberDeleteSuccessToast';
import { generateMemberEditSuccessToast } from './generateMemberEditSuccessToast';

export function useMemberRepository(member: MembershipRead | null | undefined) {
  const dispatch = useAppDispatch();
  const [createMember] = useMembersCreateMutation();
  const [deleteMember] = useMembersDestroyMutation();
  const [updateMember] = useMembersPartialUpdateMutation();
  const [restoreMember] = useMembersUndeleteCreateMutation();
  const [findMemberQuery] = useLazyMembersListQuery({});

  const handleCreateMember = useCallback(
    async (data: MembershipWrite) => {
      try {
        const newMember = await createMember({
          membership: data,
        }).unwrap();

        dispatch(show(generateMemberAddSuccessToast(newMember.first_name)));
        dispatch(setSelectedMember(null));
        dispatch(setShowMemberForm(false));

        return newMember;
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to create member',
            description: formatServerError(e),
          })
        );

        throw e;
      }
    },
    [createMember, dispatch]
  );

  const handleUpdateMember = useCallback(
    async (data: PatchedMembershipWrite) => {
      if (!member) {
        dispatch(
          show({
            type: 'error',
            title: 'Something unexpected happened',
            description: 'Missing member information during update',
          })
        );
        return;
      }

      try {
        const updatedMember = await updateMember({
          id: member.id,
          patchedMembership: data,
        }).unwrap();

        dispatch(show(generateMemberEditSuccessToast(data.first_name!)));
        dispatch(setSelectedMember(null));
        dispatch(setShowMemberForm(false));

        return updatedMember;
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to update member',
            description: formatServerError(e),
          })
        );
        throw e;
      }
    },
    [member, dispatch, updateMember]
  );

  const handleDeleteMember = useCallback(async () => {
    if (!member) {
      dispatch(
        show({
          type: 'error',
          title: 'Something unexpected happened',
          description: 'Missing member information during delete',
        })
      );
      return;
    }

    try {
      await deleteMember({
        id: member.id,
      }).unwrap();

      dispatch(show(generateMemberDeleteSuccessToast(member.first_name)));
      dispatch(setSelectedMember(null));
      dispatch(setShowMemberForm(false));

      return true;
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Unable to delete member',
          description: formatServerError(e),
        })
      );
      throw e;
    }
  }, [deleteMember, dispatch, member]);

  const handleRestoreMember = useCallback(
    async (targetMember?: MembershipRead) => {
      const memberToUse = targetMember ? targetMember : member;
      if (!memberToUse) {
        dispatch(
          show({
            type: 'error',
            title: 'Something unexpected happened',
            description: 'Missing member information during restore',
          })
        );
        return;
      }

      try {
        const restoredMember = await restoreMember({
          id: memberToUse.id,
          membership: memberToUse,
        }).unwrap();
        dispatch(
          show({
            type: 'success',
            title: 'Restored membership',
            description: `Restored membership for ${memberToUse.first_name}`,
          })
        );
        dispatch(setSelectedMember(null));
        dispatch(setShowMemberForm(false));

        return restoredMember;
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to restore member',
            description: formatServerError(e),
          })
        );

        throw e;
      }
    },
    [dispatch, member, restoreMember]
  );

  const handleFindDuplicateMemberByEmail = useCallback(
    async (email: string) => {
      try {
        const response = await findMemberQuery({
          deletedObjects: 'include',
          search: email,
        });

        const duplicateMember = response.data?.results.find(
          (m) => m.email.toLowerCase() === email.toLowerCase()
        );

        const duplicateMemberIsDeleted = !!duplicateMember?.deleted_at;
        return {
          duplicateMember,
          duplicateMemberIsDeleted,
        };
      } catch (e) {
        // This isn't a user-facing operation, so just log the error
        console.error('Unable to query for member', e);
      }
    },
    [findMemberQuery]
  );

  const handleFindDuplicateMemberByName = useCallback(
    async (firstName: string, lastName: string) => {
      try {
        const nameResponse = await findMemberQuery({
          deletedObjects: 'include',
          search: `${firstName.trim()} ${lastName.trim()}`,
        });

        const duplicateMember = nameResponse.data?.results.find(
          (m) =>
            m.first_name.trim().toLowerCase() ===
              firstName.trim().toLowerCase() &&
            m.last_name.trim().toLowerCase() === lastName.trim().toLowerCase()
        );

        const duplicateMemberIsDeleted = !!duplicateMember?.deleted_at;
        return {
          duplicateMember,
          duplicateMemberIsDeleted,
        };
      } catch (e) {
        // This isn't a user-facing operation, so just log the error
        console.error('Unable to query for member', e);
      }
    },
    [findMemberQuery]
  );

  return {
    create: handleCreateMember,
    update: handleUpdateMember,
    delete: handleDeleteMember,
    restore: handleRestoreMember,
    findDuplicateByEmail: handleFindDuplicateMemberByEmail,
    findDuplicateByName: handleFindDuplicateMemberByName,
  };
}
